import { IconButton, InputAdornment, TextField } from '@mui/material'
import tw from 'twin.macro'
import { CircleCancelMinor, SearchMinor } from '@shopify/polaris-icons'
import React, { ChangeEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'

/**
 * Table search box, matching `apps/dashboard`'s `components/shared/table/TableSearch`.
 *
 * The `rounded-lg overflow-hidden` wrapper is load-bearing: the outlined input's own corners are
 * rounded while its background is painted by this wrapper, so clipping here is what stops white
 * squares showing through the corners.
 */
interface Props {
  searchQuery: string
  onChangeSearchQuery: (search: string) => void
  className?: string
  width?: number
  /**
   * Fixed input height. Left undefined the field keeps MUI's `size="small"` intrinsic height
   * (47px with the search adornment), matching dashboard. Pass a value to line the field up with
   * neighbouring buttons.
   */
  height?: number
  /**
   * Dashboard debounces at 700ms because its search hits the API. Callers that filter in memory
   * should pass something much smaller so local filtering still feels immediate.
   */
  debounceMs?: number
  inputDataTestId?: string
  clearButtonDataTestId?: string
}

const TableSearchComponent = ({
  searchQuery,
  onChangeSearchQuery,
  className,
  width = 400,
  height,
  debounceMs = 700,
  inputDataTestId,
  clearButtonDataTestId,
}: Props) => {
  const [searchValue, setSearchValue] = useState<string>(searchQuery)

  // Keeps the debounced caller in sync with the latest `onChangeSearchQuery` without
  // recreating (and losing the pending debounce timer of) the debounced function itself.
  const onChangeSearchQueryRef = useRef(onChangeSearchQuery)

  useEffect(() => {
    onChangeSearchQueryRef.current = onChangeSearchQuery
  }, [onChangeSearchQuery])

  const searchDebounce = useMemo(() => {
    return _.debounce((value: string) => {
      onChangeSearchQueryRef.current(value)
    }, debounceMs)
  }, [debounceMs])

  useEffect(() => {
    return () => {
      searchDebounce.cancel()
    }
  }, [searchDebounce])

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }, [])

  useEffect(() => {
    searchDebounce(searchValue)
  }, [searchValue, searchDebounce])

  // Syncs an external reset/change of the controlled `searchQuery` prop into local state,
  // without clobbering text the user is actively typing while a debounced call is pending.
  const previousSearchQueryRef = useRef(searchQuery)

  useEffect(() => {
    if (searchQuery === previousSearchQueryRef.current) {
      return
    }

    previousSearchQueryRef.current = searchQuery

    if (searchQuery !== searchValue) {
      setSearchValue(searchQuery)
    }
  }, [searchQuery, searchValue])

  const handleClear = useCallback(() => {
    setSearchValue('')
    onChangeSearchQuery('')
  }, [onChangeSearchQuery])

  return (
    <div
      css={[tw`bg-white flex rounded-lg flex-row overflow-hidden`, { width }]}
      className={className}
    >
      <TextField
        placeholder="Search"
        type="text"
        variant="outlined"
        fullWidth
        value={searchValue}
        size="small"
        onChange={handleSearchChange}
        inputProps={{
          'data-testid': inputDataTestId,
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchMinor width={20} />
            </InputAdornment>
          ),
          endAdornment: searchValue ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="Clear search"
                data-testid={clearButtonDataTestId}
                css={tw`mr-[-12px] rounded-none`}
                onClick={handleClear}
              >
                <CircleCancelMinor width={20} fill="#B4AFC0" />
              </IconButton>
            </InputAdornment>
          ) : null,
          sx: {
            '&:not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
              border: '1px solid var(--Shades-Grey-100, #EDEDED)',
            },
            ...(height === undefined ? {} : { height, alignItems: 'center' }),
          },
        }}
      />
    </div>
  )
}

export const TableSearch = memo(TableSearchComponent)
